import React, { useState, useEffect, useRef } from "react";
import { Zap, FileText, ShieldCheck, RefreshCw, CircleCheck as CheckCircle2, Play } from 'lucide-react';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const checkmarks = [{
  text: "Locally sourced from Sydney factories"
}, {
  text: "Premium US-manufactured openers & motors"
}, {
  text: "Every part meets Australian safety standards"
}, {
  text: "No cheap imports or knockoff components"
}];
const trustBadges = [{
  title: "Trustpilot — 5.0 Rating",
  src: "https://goldgaragedoor.com.au/wp-content/uploads/2026/05/trust-badges-reviews.webp?v=161dbcu9l2jh"
}];
const features = [{
  Icon: Zap,
  title: "Fast Response",
  description: "Same-day service for all urgent garage door repairs."
}, {
  Icon: FileText,
  title: "Free Quotes",
  description: "Transparent pricing with no hidden call-out fees."
}, {
  Icon: ShieldCheck,
  title: "Fully Insured",
  description: "Licensed technicians and comprehensive public liability."
}, {
  Icon: RefreshCw,
  title: "Warranty",
  description: "Long-term peace of mind on all parts and labor."
}];
export default function WhyChooseUs() {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setVideoLoaded(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    if (videoRef.current) {
      observer.observe(videoRef.current);
    }
    return () => observer.disconnect();
  }, []);
  return <section className="bg-white py-[40px] -mt-20">
      <div className="max-w-[1240px] mx-auto px-4">

        <div className="mb-12 text-left">
          <div className="w-10 h-[2px] bg-[#9A6400] mb-4" />
          <span className="block text-[11px] font-bold uppercase tracking-[0.1em] text-[#9A6400] mb-2 font-['Manrope']">
            WHY GOLD GARAGE DOOR
          </span>
          <h2 className="text-[38px] font-extrabold text-[#1a2a4a] leading-tight mb-4 font-['Manrope']">
            The Difference Is in the Detail
          </h2>
          <p className="text-[16px] text-[#5d6e8a] max-w-[560px] leading-relaxed">
            We don't cut corners — on parts, workmanship, or customer service. That's why thousands of Sydney homeowners trust us.
          </p>
        </div>


        <div className="flex flex-col lg:flex-row gap-8 items-start">

          <div className="w-full lg:w-[55%]">
            <div className="block sm:flex gap-8 items-start">

              <div className="w-full max-w-[240px] sm:max-w-[260px] mx-auto sm:mx-0 mb-8 sm:mb-0 shrink-0">
                <div className="rounded-xl border border-[#e5e7eb] shadow-md overflow-hidden" ref={videoRef}>
                  <AspectRatio ratio={9 / 16}>
                    {videoLoaded ? <iframe src="https://www.youtube-nocookie.com/embed/o0NVok-yoq4?autoplay=1" title="Gold Garage Door Quality" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /> : <div role="button" tabIndex={0} aria-label="Play video about Gold Garage Door quality" className="relative w-full h-full group cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#9A6400]" onClick={() => setVideoLoaded(true)} onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setVideoLoaded(true);
                    }
                  }}>
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/hqdefault.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/hqdefault.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1681701/img/hqdefault.png-wvc-sizes" alt="Video thumbnail" width="260" height="462" className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                          <div className="w-16 h-16 rounded-lg bg-[#9A6400] flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform" data-icon-container="lucide-do-not-replace">
                            {/** @icon do-not-replace: lucide Play icon, must stay inline SVG */}
                            <Play className="w-8 h-8 text-white fill-current" aria-hidden="true" />
                          </div>
                        </div>
                      </div>}
                  </AspectRatio>
                </div>
              </div>


              <div className="flex-1">
                <h3 className="text-[20px] font-bold text-[#1a2a4a] mb-3">Australian &amp; US Quality Parts</h3>
                <p className="text-[15px] text-[#5d6e8a] mb-6 leading-relaxed">
                  We source all our parts from local Sydney factories and trusted US manufacturers. No cheap imports — every spring, cable, opener, and panel we install meets Australian safety standards.
                </p>
                <ul className="space-y-3">
                  {checkmarks.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-[14px] font-medium text-[#1a2a4a]">
                      <span data-icon-container="lucide-do-not-replace" className="shrink-0">
                        {/** @icon do-not-replace: lucide CheckCircle, must stay inline SVG */}
                        <CheckCircle2 className="w-5 h-5 text-[#9A6400]" aria-hidden="true" />
                      </span>
                      <span>{item.text}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>


          <div className="w-full lg:w-[45%] flex flex-col items-center">
            <div className="grid grid-cols-1 gap-4 sm:gap-5">
              {trustBadges.map((badge, i) => <Card key={i} data-index={i} className="w-full sm:w-[450px] h-auto bg-transparent border-none py-0 flex items-center justify-center p-5 text-center !shadow-none">
                  <CardContent className="p-0">
                    <img src={badge.src} alt="Customer reviews for Gold Garage Door" width="650" height="350" loading="lazy" className="max-w-[450px] bg-transparent !h-auto !w-full sm:!h-[350px] sm:!w-[650px] max-h-[300px] sm:max-h-none" />
                  </CardContent>
                </Card>)}
            </div>
            <p className="mt-6 text-[12px] text-[#9ca3af] text-center">
              Verified on Australia's leading trade platforms
            </p>
          </div>
        </div>


        <div className="mt-8 bg-[#f7f9fc] rounded-xl p-6 md:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-6 items-center">
            {features.map(({
            Icon,
            title,
            description
          }, i) => <React.Fragment key={i}>
                <Card data-index={i} className="bg-transparent border-none py-0 items-center text-center !shadow-none">
                  <CardContent className="p-0 flex flex-col items-center">
                    <div className="w-9 h-9 rounded-lg bg-[#9A6400]/10 flex items-center justify-center mb-3" data-icon-container="lucide-do-not-replace">
                      {/** @icon do-not-replace: lucide feature icon, must stay inline SVG */}
                      <Icon className="w-[18px] h-[18px] text-[#9A6400]" aria-hidden="true" />
                    </div>
                    <h4 className="text-[15px] font-bold text-[#1a2a4a] mb-1">
                      {title}
                    </h4>
                    <p className="text-[13px] text-[#5d6e8a] leading-tight">
                      {description}
                    </p>
                  </CardContent>
                </Card>
                {i < features.length - 1 && <Separator orientation="vertical" className="hidden lg:block h-12 bg-[#e5e7eb]" />}
              </React.Fragment>)}
          </div>
        </div>
      </div>
    </section>;
}